import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface TabItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  /** Count shown as a trailing pill — pending offers, unread reports. */
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  /** `line` for in-panel sections, `enclosed` for top-level view switching. */
  variant?: 'line' | 'enclosed';
  className?: string;
}

/**
 * Horizontal tab strip.
 *
 * Roving-tabindex keyboard support: only the active tab is tabbable, and the
 * arrow keys move between tabs — the ARIA pattern users expect. Wire the panels
 * yourself with `role="tabpanel"` and `aria-labelledby={`tab-${id}`}`.
 */
export function Tabs({ items, value, onChange, variant = 'line', className }: TabsProps) {
  const enabled = items.filter((item) => !item.disabled);

  const move = (direction: 1 | -1) => {
    const index = enabled.findIndex((item) => item.id === value);
    const next = enabled[(index + direction + enabled.length) % enabled.length];
    if (next) onChange(next.id);
  };

  return (
    <div
      role="tablist"
      className={cn(
        'flex items-center gap-1',
        variant === 'line' && 'border-b border-edge-subtle',
        variant === 'enclosed' && 'rounded-control border border-edge bg-surface-base p-1',
        className,
      )}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          move(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          move(-1);
        }
      }}
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            id={`tab-${item.id}`}
            role="tab"
            type="button"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap font-display text-label uppercase',
              'transition-colors duration-[var(--sg-duration-fast)]',
              'disabled:pointer-events-none disabled:opacity-40',
              variant === 'line' && [
                '-mb-px border-b-2 px-3 py-2',
                active
                  ? 'border-silicate text-silicate-bright'
                  : 'border-transparent text-content-muted hover:text-content',
              ],
              variant === 'enclosed' && [
                'flex-1 justify-center rounded-[4px] px-3 py-1.5',
                active
                  ? 'bg-surface-raised text-content shadow-inset-edge'
                  : 'text-content-muted hover:text-content',
              ],
            )}
          >
            {item.icon}
            {item.label}
            {item.count !== undefined ? (
              <span
                data-sg-numeric
                className={cn(
                  'ml-0.5 rounded-pill px-1.5 py-px text-label-sm',
                  active ? 'bg-silicate/20 text-silicate-bright' : 'bg-surface-raised text-content-muted',
                )}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
